<template>
  <div class="demo-page">
    <div class="header">
      <h1>DinosaurGame - Performance Optimizations Demo</h1>
      <p>Demonstrating the improvements made to reduce lag and increase difficulty</p>
    </div>
    
    <div class="demo-container">
      <div class="demo-section">
        <h2>🚀 Optimized DinosaurGame</h2>
        <p class="description">
          The game has been optimized for better performance and enhanced with variable obstacle heights.
        </p>
        
        <DinosaurGame 
          :instruction-text="'Test the optimized game with variable obstacles!'"
          :flamou-image="flamouImage"
        />
        
        <div class="improvements-list">
          <h3>✨ Improvements Made:</h3>
          <ul>
            <li>🏃‍♂️ <strong>Reduced Lag:</strong> Removed expensive drawing operations (moving ground dots)</li>
            <li>🎨 <strong>Simplified Visuals:</strong> Replaced gradients with solid colors for better performance</li>
            <li>📐 <strong>Variable Heights:</strong> 4 different obstacle heights (20px, 30px, 40px, 50px)</li>
            <li>🎯 <strong>Increased Difficulty:</strong> Higher spawn rate and faster progression</li>
            <li>🎮 <strong>Better Gameplay:</strong> More challenging and engaging experience</li>
          </ul>
        </div>
      </div>
    </div>
    
    <div class="technical-details">
      <h3>🔧 Technical Changes:</h3>
      <div class="changes-grid">
        <div class="change-item">
          <h4>Performance</h4>
          <p>Removed <code>drawMovingGround()</code> function that caused lag</p>
        </div>
        <div class="change-item">
          <h4>Visuals</h4>
          <p>Simplified graphics with solid colors instead of gradients</p>
        </div>
        <div class="change-item">
          <h4>Obstacles</h4>
          <p>Added 4 heights with different colors for visual distinction</p>
        </div>
        <div class="change-item">
          <h4>Difficulty</h4>
          <p>Increased spawn rate and improved progression curve</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DinosaurGame from '@/components/DinosaurGame.vue'

export default {
  name: 'DinosaurGameDemo',
  components: {
    DinosaurGame
  },
  data() {
    return {
      flamouImage: require('@/assets/flamou/happy.png')
    }
  }
}
</script>

<style scoped>
.demo-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Arial', sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  color: white;
}

.header {
  text-align: center;
  margin-bottom: 40px;
}

.header h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.header p {
  font-size: 1.2rem;
  opacity: 0.9;
}

.demo-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  padding: 30px;
  margin-bottom: 30px;
  color: #333;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
}

.demo-section h2 {
  color: #4285f4;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.description {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 20px;
}

.improvements-list {
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  border-left: 4px solid #4285f4;
}

.improvements-list h3 {
  color: #4285f4;
  margin-bottom: 15px;
}

.improvements-list ul {
  list-style: none;
  padding: 0;
}

.improvements-list li {
  margin: 12px 0;
  padding: 8px 0;
  font-size: 1.05rem;
  border-bottom: 1px solid #e9ecef;
}

.improvements-list li:last-child {
  border-bottom: none;
}

.technical-details {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 25px;
  backdrop-filter: blur(10px);
}

.technical-details h3 {
  margin-bottom: 20px;
  text-align: center;
}

.changes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.change-item {
  background: rgba(255, 255, 255, 0.1);
  padding: 20px;
  border-radius: 10px;
  text-align: center;
}

.change-item h4 {
  color: #ffeb3b;
  margin-bottom: 10px;
  font-size: 1.2rem;
}

.change-item p {
  font-size: 0.95rem;
  opacity: 0.9;
}

code {
  background: rgba(0,0,0,0.2);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

@media (max-width: 768px) {
  .demo-page {
    padding: 10px;
  }
  
  .header h1 {
    font-size: 2rem;
  }
  
  .demo-container {
    padding: 20px;
  }
  
  .changes-grid {
    grid-template-columns: 1fr;
  }
}
</style>